/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useEffect, useRef, useState } from 'react';

import { cssMap as cssMapCompiled, jsx, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

/* ────────────────────────────────────────────────────────
 * Keyframe animations
 * ──────────────────────────────────────────────────────── */

/** Slow drifting motion for the three glow blobs — large percentage-based travel */
const glowDriftA = keyframes({
	'0%': { transform: 'translate(-30%, 4%)' },
	'25%': { transform: 'translate(20%, -6%)' },
	'50%': { transform: 'translate(55%, 8%)' },
	'75%': { transform: 'translate(10%, -4%)' },
	'100%': { transform: 'translate(-30%, 4%)' },
});

const glowDriftB = keyframes({
	'0%': { transform: 'translate(35%, -2%)' },
	'25%': { transform: 'translate(-15%, 6%)' },
	'50%': { transform: 'translate(-50%, -4%)' },
	'75%': { transform: 'translate(5%, 8%)' },
	'100%': { transform: 'translate(35%, -2%)' },
});

const glowDriftC = keyframes({
	'0%': { transform: 'translate(-15%, 6%)' },
	'20%': { transform: 'translate(40%, -8%)' },
	'45%': { transform: 'translate(-45%, 4%)' },
	'70%': { transform: 'translate(25%, 8%)' },
	'100%': { transform: 'translate(-15%, 6%)' },
});

/** One-shot ripple — expanding box-shadow with morphing border-radius */
const maxRipple = keyframes({
	'0%': {
		borderRadius: token('radius.xlarge'),
		boxShadow: '0 0 0 0 rgba(120, 125, 135, 0.18)',
	},
	'30%': {
		borderRadius: '18px',
		boxShadow: '0 0 18px 50px rgba(120, 125, 135, 0.06)',
	},
	'60%': {
		borderRadius: '40px',
		boxShadow: '0 0 80px 110px rgba(120, 125, 135, 0.015)',
	},
	'100%': {
		borderRadius: '9999px',
		boxShadow: '0 0 280px 200px rgba(120, 125, 135, 0)',
	},
});

/* ────────────────────────────────────────────────────────
 * Beam keyframes — one per dot opacity level
 * Each must be a static declaration for @compiled/react
 * babel plugin to extract at build time.
 * ──────────────────────────────────────────────────────── */

const beamTravel100 = keyframes({
	'0%': { offsetDistance: '22%', opacity: 0 },
	'8%': { opacity: 1 },
	'35%': { opacity: 1 },
	'100%': { offsetDistance: '122%', opacity: 0 },
});

const beamTravel60 = keyframes({
	'0%': { offsetDistance: '22%', opacity: 0 },
	'8%': { opacity: 0.6 },
	'35%': { opacity: 0.6 },
	'100%': { offsetDistance: '122%', opacity: 0 },
});

const beamTravel35 = keyframes({
	'0%': { offsetDistance: '22%', opacity: 0 },
	'8%': { opacity: 0.35 },
	'35%': { opacity: 0.35 },
	'100%': { offsetDistance: '122%', opacity: 0 },
});

const beamTravel20 = keyframes({
	'0%': { offsetDistance: '22%', opacity: 0 },
	'8%': { opacity: 0.2 },
	'35%': { opacity: 0.2 },
	'100%': { offsetDistance: '122%', opacity: 0 },
});

const beamTravel10 = keyframes({
	'0%': { offsetDistance: '22%', opacity: 0 },
	'8%': { opacity: 0.1 },
	'35%': { opacity: 0.1 },
	'100%': { offsetDistance: '122%', opacity: 0 },
});

/* ────────────────────────────────────────────────────────
 * Compiled styles
 * ──────────────────────────────────────────────────────── */

/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- gradient glow colors and shape tokens have no token equivalent */
const compiledStyles = cssMapCompiled({
	/** Wrapper that establishes positioning context for all overlays */
	container: {
		position: 'relative',
	},

	/* ── Persisted glow ── */
	glowContainer: {
		position: 'absolute',
		top: '-32px',
		left: '4px',
		right: '4px',
		height: '50%',
		pointerEvents: 'none',
		isolation: 'isolate',
		overflow: 'hidden',
		filter: 'blur(18px) saturate(1.3)',
		WebkitMaskImage:
			'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.35) 80%, transparent 100%)',
		maskImage:
			'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.35) 80%, transparent 100%)',
	},
	glowBlobBase: {
		position: 'absolute',
		inset: 0,
		willChange: 'transform',
		mixBlendMode: 'plus-lighter',
	},
	glowBlobA: {
		background:
			'radial-gradient(35% 110% at 20% 115%, rgba(210,214,222,0.7) 0%, rgba(210,214,222,0.35) 40%, transparent 75%)',
		animationName: glowDriftA,
		animationDuration: '22s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',
	},
	glowBlobB: {
		background:
			'radial-gradient(35% 110% at 80% 115%, rgba(93,155,255,0.7) 0%, rgba(93,155,255,0.35) 40%, transparent 75%)',
		animationName: glowDriftB,
		animationDuration: '28s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',
	},
	glowBlobC: {
		background:
			'radial-gradient(28% 100% at 50% 120%, #c478ff 0%, rgba(196,120,255,0.45) 40%, transparent 78%)',
		animationName: glowDriftC,
		animationDuration: '34s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',
	},

	/* ── Ripple ── */
	ripple: {
		position: 'absolute',
		inset: 0,
		pointerEvents: 'none',
		borderRadius: '12px',
		animationName: maxRipple,
		animationDuration: '1050ms',
		animationTimingFunction: 'linear',
		animationFillMode: 'forwards',
	},

	/* ── Beam ── */
	beamContainer: {
		position: 'absolute',
		inset: 0,
		zIndex: 20,
		pointerEvents: 'none',
		overflow: 'visible',
	},
	beamDotBase: {
		position: 'absolute',
		top: 0,
		left: 0,
		borderRadius: '9999px',
		backgroundColor: token('color.text'),
		animationDuration: '850ms',
		animationTimingFunction: 'linear',
		animationFillMode: 'forwards',
	},
	beamLead: {
		width: '6px',
		height: '6px',
		boxShadow: '0 0 6px 1px rgba(11, 18, 14, 0.4)',
		animationName: beamTravel100,
		animationDelay: '0ms',
	},
	beamTrail1: {
		width: '5px',
		height: '5px',
		filter: 'blur(0.5px)',
		animationName: beamTravel60,
		animationDelay: '40ms',
	},
	beamTrail2: {
		width: '4px',
		height: '4px',
		filter: 'blur(1px)',
		animationName: beamTravel35,
		animationDelay: '80ms',
	},
	beamTrail3: {
		width: '4px',
		height: '4px',
		filter: 'blur(1.5px)',
		animationName: beamTravel20,
		animationDelay: '120ms',
	},
	beamTrail4: {
		width: '3px',
		height: '3px',
		filter: 'blur(2px)',
		animationName: beamTravel10,
		animationDelay: '160ms',
	},
});
/* eslint-enable @atlaskit/design-system/ensure-design-token-usage */

/* ────────────────────────────────────────────────────────
 * Offset-path inline style (not supported by @compiled/react)
 * ──────────────────────────────────────────────────────── */

const beamOffsetStyle: React.CSSProperties = {
	offsetPath: 'inset(0 round 12px)',
	offsetRotate: '0deg',
	offsetDistance: '0%',
};

/* ────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────── */

type TaskModePromptAnimationProps = {
	children: React.ReactNode;
	isTaskMode: boolean;
	/**
	 * Keeps the wrapper mounted outside task mode so a mode change does not remount
	 * the editor subtree (which transiently destroys @mention nodes). Driven by the
	 * effective agent-mentions capability at production call sites; when false
	 * the component returns the bare fragment, matching the pre-feature DOM shape.
	 */
	shouldKeepPromptMounted: boolean;
	triggerAnimation: number;
};

/**
 * Wrapper that adds visual animation effects to the prompt input box
 * when task/max mode is active:
 *
 * 1. Persisted glow — three slowly drifting radial gradient blobs above the box
 * 2. Ripple — one-shot expanding box-shadow when entering task mode
 * 3. Beam — small dots racing around the border using CSS offset-path
 */
export const TaskModePromptAnimation = ({
	children,
	isTaskMode,
	shouldKeepPromptMounted,
	triggerAnimation,
}: TaskModePromptAnimationProps): JSX.Element => {
	const [rippleKey, setRippleKey] = useState(0);
	const prevTriggerRef = useRef(triggerAnimation);

	useEffect(() => {
		if (triggerAnimation > prevTriggerRef.current && isTaskMode) {
			setRippleKey((k) => k + 1);
		}
		prevTriggerRef.current = triggerAnimation;
	}, [triggerAnimation, isTaskMode]);

	// Reset rippleKey when leaving task mode so that a programmatic
	// isTaskMode false→true toggle (store reset, conversation switch, etc.)
	// does not remount stale ripple/beam DOM nodes and replay their CSS animations.
	useEffect(() => {
		if (!isTaskMode) {
			setRippleKey(0);
		}
	}, [isTaskMode]);

	const shouldRenderWrapper = shouldKeepPromptMounted || isTaskMode;

	if (!shouldRenderWrapper) {
		return <>{children}</>;
	}

	return (
		<div css={compiledStyles.container}>
			{/* Persisted glow — visible whenever task mode is active, rendered BELOW children */}
			{isTaskMode && (
				<div css={compiledStyles.glowContainer} aria-hidden="true" data-testid="prompt-max-glow">
					<div css={[compiledStyles.glowBlobBase, compiledStyles.glowBlobA]} />
					<div css={[compiledStyles.glowBlobBase, compiledStyles.glowBlobB]} />
					<div css={[compiledStyles.glowBlobBase, compiledStyles.glowBlobC]} />
				</div>
			)}

			{/* One-shot ripple — fires when entering task mode */}
			{rippleKey > 0 && (
				<div
					key={`ripple-${rippleKey}`}
					css={compiledStyles.ripple}
					aria-hidden="true"
					data-testid="prompt-max-ripple"
				/>
			)}

			{/* One-shot beam — dots traveling along the border via offset-path */}
			{/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- offset-path properties not supported by @compiled/react */}
			{rippleKey > 0 && (
				<div
					key={`beam-${rippleKey}`}
					css={compiledStyles.beamContainer}
					aria-hidden="true"
					data-testid="prompt-max-beam"
				>
					<div
						css={[compiledStyles.beamDotBase, compiledStyles.beamTrail4]}
						style={beamOffsetStyle}
					/>
					<div
						css={[compiledStyles.beamDotBase, compiledStyles.beamTrail3]}
						style={beamOffsetStyle}
					/>
					<div
						css={[compiledStyles.beamDotBase, compiledStyles.beamTrail2]}
						style={beamOffsetStyle}
					/>
					<div
						css={[compiledStyles.beamDotBase, compiledStyles.beamTrail1]}
						style={beamOffsetStyle}
					/>
					<div
						css={[compiledStyles.beamDotBase, compiledStyles.beamLead]}
						style={beamOffsetStyle}
					/>
				</div>
			)}
			{/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */}

			{/* Children (prompt box) — renders above overlays via DOM order + isolation */}
			{children}
		</div>
	);
};
